import { useGetTopBottomPerformingIdeasQuery } from '../../../../../apis/statistics/statistics-api.api.ts'
import { PerformingIdea } from '../../../../../apis/statistics/statistics-api.def.ts'
import ReactECharts from 'echarts-for-react'
import { DashboardChartBox } from '../dashboard-chart-box.tsx'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectDashboardInput } from '../../../../../redux/slices/dashboard-input.slice.ts'
import dayjs from 'dayjs'
import echartsTheme from '../../../../../styles/echarts/echarts-theme.json'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'

interface TopBottomPerformingIdeasChartProps {
  bookId: string
}

export const TopBottomPerformingIdeasChart: React.FC<
  TopBottomPerformingIdeasChartProps
> = ({ bookId }) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const dashboardInput = useAppSelector(selectDashboardInput)
  const { data } = useGetTopBottomPerformingIdeasQuery({
    bookId: bookId,
    startDate: dayjs(dashboardInput.dateRange[0]).format('YYYY-MM-DD'),
    endDate: dayjs(dashboardInput.dateRange[1]).format('YYYY-MM-DD'),
  })

  if (
    !data ||
    (data && data.data.topIdeas.length === 0) ||
    data.data.bottomIdeas.length === 0
  ) {
    return <></>
  }

  const pnlAllZero = (ideas: PerformingIdea[]) =>
    ideas.every((idea) => idea.totalPnl === 0)

  if (pnlAllZero(data.data.topIdeas) && pnlAllZero(data.data.bottomIdeas)) {
    return (
      <>
        <div>
          All ideas have 0 pnl, cannot create Top/Bottom Performing Ideas
        </div>
      </>
    )
  }

  const Legend = () => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '20px',
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}
        >
          <div
            style={{
              borderRadius: '4px',
              width: '30px',
              height: '15px',
              backgroundColor: '#67b365',
              marginRight: '5px',
            }}
          ></div>
          <span>Long</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              borderRadius: '4px',
              width: '30px',
              height: '15px',
              backgroundColor: '#f26466',
              marginRight: '5px',
            }}
          ></div>
          <span>Short</span>
        </div>
      </div>
    )
  }

  const roundToNearest10000 = (value: number) => {
    if (value > 10000) {
      return Math.round(value / 10000) * 10000
    } else {
      return Math.round(value)
    }
  }

  const topIdeas = [...data.data.topIdeas]
    .filter((idea) => idea.totalPnl > 0)
    .sort((a, b) => a.totalPnl - b.totalPnl)
  const bottomIdeas = [...data.data.bottomIdeas]
    .filter((idea) => idea.totalPnl < 0)
    .sort((a, b) => b.totalPnl - a.totalPnl)

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: { data: number }[]) => {
        return Math.round(params[0].data).toLocaleString()
      },
    },
    grid: [
      { left: '3%', right: '52%', bottom: '3%', containLabel: true },
      { left: '52%', right: '3%', bottom: '3%', containLabel: true },
    ],
    xAxis: [
      {
        type: 'value',
        min: roundToNearest10000(
          Math.min(Math.min(...bottomIdeas.map((i) => i.totalPnl)) * 1.1, -10)
        ),
        max: 0,
        gridIndex: 0,
        axisLabel: {
          color: themeStyle.sumText,
        },
      },
      {
        type: 'value',
        min: 0,
        max: roundToNearest10000(
          Math.max(Math.max(...topIdeas.map((i) => i.totalPnl)) * 1.1, 10)
        ),
        gridIndex: 1,
        axisLabel: {
          color: themeStyle.sumText,
        },
      },
    ],
    yAxis: [
      {
        type: 'category',
        data: [...bottomIdeas.map((i) => i.bbgCompositeKey)],
        name: 'Worst Ideas',
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        axisLabel: {
          color: themeStyle.sumText,
        },
        position: 'right',
        gridIndex: 0,
      },
      {
        type: 'category',
        data: [...topIdeas.map((i) => i.bbgCompositeKey)],
        name: 'Best Ideas',
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        axisLabel: {
          color: themeStyle.sumText,
        },
        position: 'left',
        gridIndex: 1,
      },
    ],
    series: [
      {
        type: 'bar',
        stack: 'total',
        label: {
          show: true,
          position: 'left',
          formatter: function (params: { value: number }) {
            return Math.round(params.value).toLocaleString()
          },
          color: themeStyle.sumText,
        },
        emphasis: {
          focus: 'series',
        },
        data: [...bottomIdeas.map((i) => i.totalPnl)],
        itemStyle: {
          color: function (params: { dataIndex: number }) {
            const idea = bottomIdeas[params.dataIndex]
            return idea.direction === 'Long' ? '#67b365' : '#f26466'
          },
        },
        xAxisIndex: 0,
        yAxisIndex: 0,
      },
      {
        type: 'bar',
        stack: 'total',
        label: {
          show: true,
          position: 'right',
          formatter: function (params: { value: number }) {
            return Math.round(params.value).toLocaleString()
          },
          color: themeStyle.sumText,
        },
        emphasis: {
          focus: 'series',
        },
        itemStyle: {
          color: function (params: { dataIndex: number }) {
            const idea = topIdeas[params.dataIndex]
            return idea.direction === 'Long' ? '#67b365' : '#f26466'
          },
        },
        data: [...topIdeas.map((i) => i.totalPnl)],
        xAxisIndex: 1,
        yAxisIndex: 1,
      },
    ],
  }

  return (
    <>
      <DashboardChartBox title={t('dashboard.top_bottom_performing_ideas')}>
        <ReactECharts option={option} theme={echartsTheme.themeName} />
        <Legend />
      </DashboardChartBox>
    </>
  )
}
